import {
  GET_ACCOUNT_SCHOOL_SUBSCRIPTION,
  GET_ACADEMIC_YEARS,
  GET_FILTERED_SCHOOL_CLASSES_SUPERUSER,
  GET_SCHOOL,
  GET_SCHOOL_CLASS_DATA,
  GET_SCHOOL_CLASSES,
  GET_SCHOOL_GROUP,
  GET_STUDENT,
  GET_STUDENTS,
  GET_TEACHER,
  GET_TEACHERS,
  SEARCH_SCHOOL_GROUPS,
  SEARCH_SCHOOLS,
  GET_SCHOOL_PHASE_OPTIONS,
  SEARCH_LOCAL_AUTHORITIES,
} from "./Queries";
import {
  MANAGE_ACCOUNT_SCHOOL_SUBSCRIPTION,
  ASSIGN_STUDENTS_MUTATION,
  ASSIGN_TEACHERS_MUTATION,
  EXTRACT_STUDENTS_FROM_FILE,
  CREATE_SCHOOL_CLASS_MUTATION,
  CREATE_STUDENT_MUTATION,
  CREATE_TEACHER_MUTATION,
  REMOVE_STUDENTS_MUTATION,
  REMOVE_TEACHERS_MUTATION,
  BULK_UPLOAD_STUDENTS_MUTATION,
  UPDATE_STUDENT_MUTATION,
  CONFIGURE_SCHOOL_SUBSCRIPTION,
  OFFBOARD_SCHOOL_MUTATION,
  UPDATE_SCHOOL_SUBSCRIPTION_LICENSES,
  UPDATE_SCHOOL_CLASS_MUTATION,
  MERGE_SCHOOL_CLASS_MUTATION,
} from "./Mutations";

export class SchoolsClient {
  constructor(client) {
    this.client = client;
  }

  async getAccountSchoolSubscription() {
    const { data } = await this.client.query({
      query: GET_ACCOUNT_SCHOOL_SUBSCRIPTION,
      fetchPolicy: "network-only",
    });
    return data.accountSchoolSubscription;
  }
  async manageAccountSchoolSubscription(variables) {
    const { data } = await this.client.mutate({
      mutation: MANAGE_ACCOUNT_SCHOOL_SUBSCRIPTION,
      variables,
    });
    return data.manageAccountSchoolSubscription;
  }

  async createSchoolClass({ name, academicYear, yearGroup, subject, school }) {
    const res = await this.client.mutate({
      mutation: CREATE_SCHOOL_CLASS_MUTATION,
      variables: { name, academicYear, yearGroup, subject, school },
    });
    return res.data.createSchoolClass;
  }
  async createStudent({
    studentIdentifier,
    firstName,
    lastName,
    school,
    yearGroup,
    email,
    schoolClass,
  }) {
    const res = await this.client.mutate({
      mutation: CREATE_STUDENT_MUTATION,
      variables: {
        studentIdentifier,
        firstName,
        lastName,
        school,
        yearGroup,
        email,
        schoolClass,
      },
    });
    return res.data.createStudent;
  }
  async updateStudent({
    student,
    studentIdentifier,
    firstName,
    lastName,
    yearGroup,
    email,
  }) {
    const res = await this.client.mutate({
      mutation: UPDATE_STUDENT_MUTATION,
      variables: {
        student,
        studentIdentifier,
        firstName,
        lastName,
        yearGroup,
        email,
      },
    });
    return res.data.updateStudent;
  }
  async createTeacher({ firstName, lastName, school, email }) {
    const res = await this.client.mutate({
      mutation: CREATE_TEACHER_MUTATION,
      variables: { firstName, lastName, school, email },
    });
    return res.data.createTeacher;
  }
  async assignStudentsToClass({ students, schoolClass }) {
    const res = await this.client.mutate({
      mutation: ASSIGN_STUDENTS_MUTATION,
      variables: { students, schoolClass },
    });
    return res.data.assignStudents;
  }

  async removeStudentsFromClass({ students, schoolClass }) {
    const res = await this.client.mutate({
      mutation: REMOVE_STUDENTS_MUTATION,
      variables: { students, schoolClass },
    });
    return res.data.removeStudents;
  }

  async assignTeachersToClass({ teachers, schoolClass }) {
    const res = await this.client.mutate({
      mutation: ASSIGN_TEACHERS_MUTATION,
      variables: { teachers, schoolClass },
    });
    return res.data.assignTeachers;
  }
  async removeTeachersFromClass({ teachers, schoolClass }) {
    const res = await this.client.mutate({
      mutation: REMOVE_TEACHERS_MUTATION,
      variables: { teachers, schoolClass },
    });
    return res.data.removeTeachers;
  }
  async extractStudentsFromFile({ file, schoolClass }) {
    const res = await this.client.mutate({
      mutation: EXTRACT_STUDENTS_FROM_FILE,
      variables: { file, schoolClass },
    });
    return res.data.extractStudents;
  }
  async bulkUploadStudents({ suggestedStudentListJson, schoolClass }) {
    const res = await this.client.mutate({
      mutation: BULK_UPLOAD_STUDENTS_MUTATION,
      variables: { suggestedStudentListJson, schoolClass },
    });
    return res.data.bulkUploadStudents;
  }

  async getSchoolClasses({
    usersOnly = false,
    school = null,
    showAllClasses = false,
    searchTerm = "",
    educationLevel = null,
    yearGroup = null,
    subjects = [],
    academicYear = null,
  }) {
    const res = await this.client.query({
      query: GET_SCHOOL_CLASSES,
      variables: {
        usersOnly,
        school,
        showAllClasses,
        searchTerm,
        educationLevel,
        yearGroup,
        subjects,
        academicYear,
      },
      fetchPolicy: "network-only",
    });
    return res.data.schoolClasses;
  }

  async getSchoolClassData({ schoolClass }) {
    const res = await this.client.query({
      query: GET_SCHOOL_CLASS_DATA,
      variables: { schoolClass },
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async getTeachers() {
    const res = await this.client.query({
      query: GET_TEACHERS,
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async getTeacher({ teacher }) {
    const res = await this.client.query({
      query: GET_TEACHER,
      variables: { teacher },
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async getStudents() {
    const res = await this.client.query({
      query: GET_STUDENTS,
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async getStudent({ student }) {
    const res = await this.client.query({
      query: GET_STUDENT,
      variables: { student },
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async searchSchools({
    searchString,
    baseSearch,
    subscribedOnly,
    subscriptionApprovalStatus,
    schoolGroup = null,
    phases = [],
    localAuthority = null,
    first = 20,
    after = null,
  }) {
    const res = await this.client.query({
      query: SEARCH_SCHOOLS,
      variables: {
        searchString,
        baseSearch,
        subscribedOnly,
        subscriptionApprovalStatus,
        schoolGroup,
        phases,
        localAuthority,
        first,
        after,
      },
    });
    return res.data.matchedSchools;
  }
  async getSchoolPhaseOptions() {
    const res = await this.client.query({
      query: GET_SCHOOL_PHASE_OPTIONS,
      fetchPolicy: "network-only",
    });
    return res.data.schoolPhaseOptions;
  }
  async searchLocalAuthorities({
    searchString,
    baseSearch = true,
    first = 20,
    after = null,
  }) {
    const res = await this.client.query({
      query: SEARCH_LOCAL_AUTHORITIES,
      variables: { searchString, baseSearch, first, after },
    });
    return res.data.matchedLocalAuthorities;
  }
  async searchSchoolGroups({
    searchString,
    baseSearch,
    subscribedOnly,
    first = 20,
    after = null,
  }) {
    const res = await this.client.query({
      query: SEARCH_SCHOOL_GROUPS,
      variables: { searchString, baseSearch, subscribedOnly, first, after },
    });
    return res.data.matchedSchoolGroups;
  }

  async getSchool({ school }) {
    const res = await this.client.query({
      query: GET_SCHOOL,
      variables: { school },
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async getFilteredSchoolClassesSuperuser({
    school,
    searchTerm,
    yearGroup,
    academicYear,
    wondeState,
    first,
    after,
  }) {
    const res = await this.client.query({
      query: GET_FILTERED_SCHOOL_CLASSES_SUPERUSER,
      variables: {
        school,
        searchTerm,
        yearGroup,
        academicYear,
        wondeState,
        first,
        after,
      },
      fetchPolicy: "network-only",
    });
    return res.data.filteredSchoolClassesSuperuser;
  }

  async getSchoolGroup({ schoolGroup }) {
    const res = await this.client.query({
      query: GET_SCHOOL_GROUP,
      variables: { schoolGroup },
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async getAcademicYears() {
    const res = await this.client.query({
      query: GET_ACADEMIC_YEARS,
      fetchPolicy: "network-only",
    });
    return res.data;
  }

  async configureSchoolSubscription({
    school,
    domains,
    subscriptionType,
    isApproved,
    acknowledgeArchive = false,
  }) {
    const res = await this.client.mutate({
      mutation: CONFIGURE_SCHOOL_SUBSCRIPTION,
      variables: {
        school,
        domains,
        subscriptionType,
        isApproved,
        acknowledgeArchive,
      },
    });
    return res.data.configureSchoolSubscription;
  }
  async offboardSchool({ school }) {
    const res = await this.client.mutate({
      mutation: OFFBOARD_SCHOOL_MUTATION,
      variables: {
        school,
      },
    });
    return res.data.offboardSchool;
  }

  async updateSchoolSubscriptionLicences({ school, items }) {
    const res = await this.client.mutate({
      mutation: UPDATE_SCHOOL_SUBSCRIPTION_LICENSES,
      variables: { school, licenseItems: items },
    });
    return res.data.updateSchoolSubscriptionLicenses;
  }
  async updateSchoolClass({
    schoolClasses,
    subject,
    yearGroup,
    wondeId,
    singleClass,
  }) {
    const res = await this.client.mutate({
      mutation: UPDATE_SCHOOL_CLASS_MUTATION,
      variables: { schoolClasses, subject, yearGroup, wondeId, singleClass },
    });
    return res.data.updateSchoolClass;
  }
  async mergeClasses({ schoolClass, mergeClass }) {
    const res = await this.client.mutate({
      mutation: MERGE_SCHOOL_CLASS_MUTATION,
      variables: { schoolClass, mergeClass },
    });
    return res.data.mergeSchoolClass;
  }
}
